'use client';
import React from 'react';
import DOMPurify from 'dompurify';

import { useTypedDispatch } from '@src/redux/store/store.types';
import { loadSvgComplete, loadSvgStart } from '@src/redux/actions/loader/loaderActions';

import { InlineSVGPropTypes } from './InlineSVG.types';
import useBreakpoint from '@src/hooks/useBreakpoint';

const InlineSVG: React.FC<InlineSVGPropTypes> = ({ style = {}, src, caption, className, renderAt }) => {
  const dispatch = useTypedDispatch();
  const figureRef = React.useRef<HTMLIFrameElement>(null);

  const { isMobile, isTablet, isDesktop } = useBreakpoint();

  // const renderAtAll = !renderAt || renderAt.length === 0;
  // console.log(renderAtAll)

  const isCorrectViewport =
    (renderAt?.includes('mobile') && isMobile) ||
    (renderAt?.includes('tablet') && isTablet) ||
    (renderAt?.includes('desktop') && isDesktop);

  React.useEffect(() => {
    if (!isCorrectViewport || !src) return;

    dispatch(loadSvgStart({ id: src }));

    const urlParts = src.split('/');
    const idAndFilename = urlParts[urlParts.length - 1];

    fetch(src)
      .then((response) => response.text())

      .catch((error) => {
        return '';
      })
      .then((svgString) => {
        const sanitizedSVGString = DOMPurify.sanitize(svgString, {
          USE_PROFILES: { svg: true },
          ADD_TAGS: [
            'use',
            'path',
            'filter',
            'feFlood',
            'feColorMatrix',
            'feOffset',
            'feGaussianBlur',
            'feComposite',
            'feBlend',
          ],
          ADD_ATTR: ['xlink:href', 'transform', 'filter', 'stdDeviation', 'dx', 'dy'],
        });

        const modifiedSVGString = sanitizedSVGString.replace(/pattern(\d)/g, `${idAndFilename}$1`);

        return new DOMParser().parseFromString(modifiedSVGString, 'image/svg+xml');
      })
      .then((parsedSVG) => {
        if (figureRef.current) {
          const existingSvg = figureRef.current.querySelector('svg');
          if (existingSvg) {
            figureRef.current.removeChild(existingSvg);
          }

          // Append parsed SVG
          figureRef.current.appendChild(parsedSVG.documentElement);

          // Append style for SVG text
          const style = document.createElement('style');
          style.textContent = `text { font-family: 'Roboto', sans-serif !important; }`;
          figureRef.current.appendChild(style);

          // Append or move the figcaption after the SVG
          let figCaption = figureRef.current.querySelector('figcaption');
          if (!figCaption && caption) {
            figCaption = document.createElement('figcaption');
            figCaption.textContent = caption;
            figCaption.className =
              'bottom-[0] z-100 mt-[1.3rem] text-center text-[1.4rem] leading-[1.8rem] text-body-copy--dark';
          } else if (figCaption && caption) {
            figCaption.textContent = caption;
          }
          if (figCaption) {
            figureRef.current.appendChild(figCaption);
          }

          dispatch(loadSvgComplete({ id: src }));
        }
      });
  }, [src, dispatch, caption, isCorrectViewport]); // Include caption in the dependency array to handle caption updates

  // now it’s safe to bail out of rendering
  if (!isCorrectViewport) return null;

  return (
    <figure
      ref={figureRef}
      style={style}
      className={`relative [&>svg]:block [&>svg]:h-auto [&>svg]:w-full [&>svg]:max-w-full ${className}`}
    />
  );
};

export default InlineSVG;
